import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Pencil, MapPin, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const emptyPoint = { name: "", address: "", schedule: "", notes: "", order: 0 };
const emptyCondition = { title: "", description: "", severity: "info", order: 0 };

export default function AdminParkingLibreSakay() {
  const [pickups, setPickups] = useState([]);
  const [dropoffs, setDropoffs] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [guidelines, setGuidelines] = useState("");

  const [pickupForm, setPickupForm] = useState(emptyPoint);
  const [editingPickup, setEditingPickup] = useState(null);
  const [dropForm, setDropForm] = useState(emptyPoint);
  const [editingDrop, setEditingDrop] = useState(null);
  const [condForm, setCondForm] = useState(emptyCondition);
  const [editingCond, setEditingCond] = useState(null);

  const load = async () => {
    const [p, d, c, g] = await Promise.all([
      api.get("/pickup-points"),
      api.get("/dropoff-locations"),
      api.get("/parking-conditions"),
      api.get("/libresakay-guidelines"),
    ]);
    setPickups(p.data); setDropoffs(d.data); setConditions(c.data);
    setGuidelines(g.data?.body || "");
  };
  useEffect(() => { load(); }, []);

  // ---- PICKUP POINTS ----
  const submitPickup = async (e) => {
    e.preventDefault();
    if (!pickupForm.name) return toast.error("Name required");
    const payload = { ...pickupForm, order: parseInt(pickupForm.order || 0, 10) };
    if (editingPickup) await api.put(`/pickup-points/${editingPickup}`, payload);
    else await api.post("/pickup-points", payload);
    toast.success(editingPickup ? "Pickup updated" : "Pickup added");
    setPickupForm(emptyPoint); setEditingPickup(null); load();
  };
  const editPickup = (p) => { setEditingPickup(p.id); setPickupForm({ name: p.name, address: p.address || "", schedule: p.schedule || "", notes: p.notes || "", order: p.order || 0 }); };
  const delPickup = async (id) => { if (!window.confirm("Delete pickup?")) return; await api.delete(`/pickup-points/${id}`); load(); };

  // ---- DROP-OFFS ----
  const submitDrop = async (e) => {
    e.preventDefault();
    if (!dropForm.name) return toast.error("Name required");
    const payload = { ...dropForm, order: parseInt(dropForm.order || 0, 10) };
    if (editingDrop) await api.put(`/dropoff-locations/${editingDrop}`, payload);
    else await api.post("/dropoff-locations", payload);
    toast.success(editingDrop ? "Drop-off updated" : "Drop-off added");
    setDropForm(emptyPoint); setEditingDrop(null); load();
  };
  const editDrop = (d) => { setEditingDrop(d.id); setDropForm({ name: d.name, address: d.address || "", schedule: d.schedule || "", notes: d.notes || "", order: d.order || 0 }); };
  const delDrop = async (id) => { if (!window.confirm("Delete drop-off?")) return; await api.delete(`/dropoff-locations/${id}`); load(); };

  // ---- GUIDELINES ----
  const saveGuidelines = async () => {
    await api.put("/libresakay-guidelines", { body: guidelines });
    toast.success("Guidelines saved");
  };

  // ---- PARKING CONDITIONS ----
  const submitCond = async (e) => {
    e.preventDefault();
    if (!condForm.title) return toast.error("Title required");
    const payload = { ...condForm, order: parseInt(condForm.order || 0, 10) };
    if (editingCond) await api.put(`/parking-conditions/${editingCond}`, payload);
    else await api.post("/parking-conditions", payload);
    toast.success(editingCond ? "Condition updated" : "Condition added");
    setCondForm(emptyCondition); setEditingCond(null); load();
  };
  const editCond = (c) => { setEditingCond(c.id); setCondForm({ title: c.title, description: c.description || "", severity: c.severity, order: c.order || 0 }); };
  const delCond = async (id) => { if (!window.confirm("Delete condition?")) return; await api.delete(`/parking-conditions/${id}`); load(); };

  const renderPointList = (items, editFn, delFn, prefix) => (
    <div className="space-y-2 mt-4" data-testid={`${prefix}-list`}>
      {items.length === 0 && <div className="text-sm text-[#6B4E9E]">None yet.</div>}
      {items.map((p) => (
        <div key={p.id} className="kf-border bg-white p-3 flex items-start justify-between gap-3" data-testid={`${prefix}-row-${p.id}`}>
          <div className="min-w-0">
            <div className="font-bold text-[#3D1E6D]">{p.name}</div>
            <div className="text-xs text-[#6B4E9E]">{p.address}</div>
            {p.schedule && <div className="text-xs text-[#3D1E6D] mt-1">⏱ {p.schedule}</div>}
            {p.notes && <div className="text-xs text-[#6B4E9E] mt-1">{p.notes}</div>}
            <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">Order: {p.order}</div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => editFn(p)} className="kf-border bg-[#FFB400] p-1.5" data-testid={`${prefix}-edit-${p.id}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={() => delFn(p.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`${prefix}-del-${p.id}`}><Trash2 className="h-3 w-3" /></button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPointForm = (form, setForm, editing, setEditing, submit, prefix) => (
    <form onSubmit={submit} className="grid sm:grid-cols-6 gap-3 mt-3" data-testid={`${prefix}-form`}>
      <div className="sm:col-span-2"><BrutalLabel>Name *</BrutalLabel><BrutalInput value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid={`${prefix}-name`} /></div>
      <div className="sm:col-span-2"><BrutalLabel>Address</BrutalLabel><BrutalInput value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} data-testid={`${prefix}-address`} /></div>
      <div className="sm:col-span-2"><BrutalLabel>Schedule</BrutalLabel><BrutalInput value={form.schedule} onChange={(e) => setForm({...form, schedule: e.target.value})} placeholder="Daily · 12 PM – 11 PM" data-testid={`${prefix}-schedule`} /></div>
      <div className="sm:col-span-4"><BrutalLabel>Notes</BrutalLabel><BrutalInput value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid={`${prefix}-notes`} /></div>
      <div className="sm:col-span-1"><BrutalLabel>Order</BrutalLabel><BrutalInput type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} data-testid={`${prefix}-order`} /></div>
      <div className="sm:col-span-1 flex items-end gap-2">
        <BrutalButton variant="primary" type="submit" data-testid={`${prefix}-save`}>{editing ? <><Save className="h-3 w-3" /> Save</> : <><Plus className="h-3 w-3" /> Add</>}</BrutalButton>
        {editing && <BrutalButton variant="outline" type="button" onClick={() => { setEditing(null); setForm(emptyPoint); }}>Cancel</BrutalButton>}
      </div>
    </form>
  );

  return (
    <div className="space-y-8" data-testid="admin-parking-libresakay">
      <SectionTitle kicker="Parking & Libreng Sakay" title="Pickup points, drop-offs, guidelines & parking conditions" />

      {/* PICKUP POINTS */}
      <BrutalCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-5 w-5 text-[#E5247C]" />
          <h3 className="display text-2xl">Libreng Sakay — Pickup Points</h3>
        </div>
        {renderPointForm(pickupForm, setPickupForm, editingPickup, setEditingPickup, submitPickup, "pickup")}
        {renderPointList(pickups, editPickup, delPickup, "pickup")}
      </BrutalCard>

      {/* DROPOFFS */}
      <BrutalCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-5 w-5 text-[#E5247C]" />
          <h3 className="display text-2xl">Libreng Sakay — Drop-off Locations</h3>
        </div>
        {renderPointForm(dropForm, setDropForm, editingDrop, setEditingDrop, submitDrop, "dropoff")}
        {renderPointList(dropoffs, editDrop, delDrop, "dropoff")}
      </BrutalCard>

      {/* GUIDELINES */}
      <BrutalCard className="p-5" data-testid="guidelines-section">
        <h3 className="display text-2xl mb-2">Libreng Sakay — General Guidelines</h3>
        <textarea
          className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 text-base font-medium min-h-[140px]"
          value={guidelines}
          onChange={(e) => setGuidelines(e.target.value)}
          placeholder="General guidelines for the Libreng Sakay shuttle service…"
          data-testid="guidelines-textarea"
        />
        <div className="mt-3">
          <BrutalButton variant="primary" onClick={saveGuidelines} data-testid="guidelines-save">
            <Save className="h-4 w-4" /> Save Guidelines
          </BrutalButton>
        </div>
      </BrutalCard>

      {/* PARKING CONDITIONS */}
      <BrutalCard className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-[#E5247C]" />
          <h3 className="display text-2xl">Parking Conditions</h3>
        </div>
        <form onSubmit={submitCond} className="grid sm:grid-cols-6 gap-3 mt-3" data-testid="parking-condition-form">
          <div className="sm:col-span-2"><BrutalLabel>Title *</BrutalLabel><BrutalInput value={condForm.title} onChange={(e) => setCondForm({...condForm, title: e.target.value})} data-testid="condition-title" /></div>
          <div className="sm:col-span-2">
            <BrutalLabel>Severity</BrutalLabel>
            <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={condForm.severity} onChange={(e) => setCondForm({...condForm, severity: e.target.value})} data-testid="condition-severity">
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="sm:col-span-1"><BrutalLabel>Order</BrutalLabel><BrutalInput type="number" value={condForm.order} onChange={(e) => setCondForm({...condForm, order: e.target.value})} data-testid="condition-order" /></div>
          <div className="sm:col-span-1 flex items-end gap-2">
            <BrutalButton variant="primary" type="submit" data-testid="condition-save">{editingCond ? <><Save className="h-3 w-3" /> Save</> : <><Plus className="h-3 w-3" /> Add</>}</BrutalButton>
            {editingCond && <BrutalButton variant="outline" type="button" onClick={() => { setEditingCond(null); setCondForm(emptyCondition); }}>Cancel</BrutalButton>}
          </div>
          <div className="sm:col-span-6">
            <BrutalLabel>Description</BrutalLabel>
            <textarea
              className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 text-base font-medium min-h-[80px]"
              value={condForm.description}
              onChange={(e) => setCondForm({...condForm, description: e.target.value})}
              placeholder="What festival-goers should know about this condition…"
              data-testid="condition-description"
            />
          </div>
        </form>
        <div className="space-y-2 mt-4">
          {conditions.length === 0 && <div className="text-sm text-[#6B4E9E]">No conditions yet.</div>}
          {conditions.map((c) => (
            <div key={c.id} className="kf-border bg-white p-3 flex items-start justify-between gap-3" data-testid={`condition-row-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color={c.severity === "critical" ? "magenta" : c.severity === "warning" ? "yellow" : "purple"}>
                    {c.severity}
                  </Badge>
                  <span className="font-bold text-[#3D1E6D]">{c.title}</span>
                </div>
                {c.description && <div className="text-xs text-[#6B4E9E] mt-1">{c.description}</div>}
                <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">Order: {c.order}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => editCond(c)} className="kf-border bg-[#FFB400] p-1.5" data-testid={`condition-edit-${c.id}`}><Pencil className="h-3 w-3" /></button>
                <button onClick={() => delCond(c.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`condition-del-${c.id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </BrutalCard>
    </div>
  );
}
